import AttachmentMenuEntrySource from '../types/AttachmentMenuEntrySource';
import { buildReplyWithContextualMenuItems } from '../utils/attachButtonMenuItemBuilder';
import { aFPCreateAttachmentHandler } from '../utils/createAttachmentHandler';
import { IContextualMenuItem } from 'office-ui-fabric-react/lib/ContextualMenu';
import { assertNever } from 'owa-assert';
import { getMRUAttachmentStore } from 'owa-attachment-suggestions';
import { CreateAttachmentsProperties } from 'owa-attachment-well-data';
import { isFeatureEnabled } from 'owa-feature-flags';
import { InlineImagePlugin } from 'owa-inline-image';
import clickAttachButton from 'owa-mail-compose-store/lib/actions/clickAttachButton';
import trySaveMessage from 'owa-mail-compose-store/lib/actions/trySaveMessage';
import ComposeViewState from 'owa-mail-compose-store/lib/store/schema/ComposeViewState';
import { ClientItem, mailStore } from 'owa-mail-store';
import { AttachmentSuggestions, ReplyWithAttachmentSuggestion } from 'owa-mail-store';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import { logUserAction, SurfaceActionItem } from 'owa-surface-actions';
import * as React from 'react';
import {
    attachmentMultiMenuEntry,
    AttachmentMultiMenuEntry,
    AttachmentMenuInfo,
} from 'owa-attachment-multi-menu-actions';

export enum AttachButton {
    COMPUTER_ATTACHMENT_KEY = 'addMyComputerAttachment',
    ONLINE_LOCATION_KEY = 'addOnlineLocationAttachment',
    UPLOAD_AND_SHARE_FROM_CLOUD_KEY = 'addUploadAndShareFromCloud',
}

type AttachmentMultiMenuMap<T> = { [K in keyof AttachmentMultiMenuEntry]: T };

/**
 * To add a button, update the AttachButton enum, update getDefaultButtonsList, then update this method.
 */
export default function getAttachmentCommandItems(
    getAttachmentLocalComputerFilePicker: () => any,
    getAttachmentLocalComputerToCloudFilePicker: () => any,
    inlineImagePlugin: InlineImagePlugin,
    viewState: ComposeViewState
): IContextualMenuItem[] {
    const openFilePicker = (filePickerToOpen: () => any) => {
        if (!viewState.itemId) {
            trySaveMessage(viewState);
        }

        filePickerToOpen()
            .getInnerComponent()
            .click();
    };

    const attachmentMultiMenuInfo: AttachmentMultiMenuMap<{
        actionName: string;
        onClick: () => void;
    }> = {
        computer: {
            actionName: 'computerAttach',
            onClick: () => openFilePicker(getAttachmentLocalComputerFilePicker),
        },
        onlineLocation: {
            actionName: 'onlineAttach',
            onClick: () => {
                clickAttachButton(
                    false /*isInline*/,
                    params => {
                        const baseProperties = { isInline: params.isInline, isHiddenInline: false };
                        const properties: CreateAttachmentsProperties = params.shouldShare
                            ? { ...baseProperties, shouldShare: true }
                            : { ...baseProperties, shouldShare: false };

                        aFPCreateAttachmentHandler(
                            params.files,
                            inlineImagePlugin,
                            viewState,
                            properties,
                            params.dataProviderType
                        );
                    },
                    viewState
                );
            },
        },
        uploadAndShareFromCloud: {
            actionName: 'computerToCloudAttach',
            onClick: () => openFilePicker(getAttachmentLocalComputerToCloudFilePicker),
        },
    };
    const isSmimeCompose = isSmimeEnabledInViewState(viewState.smimeViewState);
    const defaultButtonTypes: AttachButton[] = isSmimeCompose
        ? getSmimeAttachButtonsList()
        : getDefaultButtonsList();

    const defaultButtons: IContextualMenuItem[] = defaultButtonTypes.map<IContextualMenuItem>(
        defaultButtonType => {
            switch (defaultButtonType) {
                case AttachButton.COMPUTER_ATTACHMENT_KEY:
                    return createAttachmentContextMenuItem(
                        attachmentMultiMenuEntry.computer,
                        AttachButton.COMPUTER_ATTACHMENT_KEY,
                        attachmentMultiMenuInfo.computer.actionName,
                        attachmentMultiMenuInfo.computer.onClick
                    );
                case AttachButton.UPLOAD_AND_SHARE_FROM_CLOUD_KEY:
                    return createAttachmentContextMenuItem(
                        attachmentMultiMenuEntry.uploadAndShareFromCloud,
                        AttachButton.UPLOAD_AND_SHARE_FROM_CLOUD_KEY,
                        attachmentMultiMenuInfo.uploadAndShareFromCloud.actionName,
                        attachmentMultiMenuInfo.uploadAndShareFromCloud.onClick
                    );
                case AttachButton.ONLINE_LOCATION_KEY:
                    return createAttachmentContextMenuItem(
                        attachmentMultiMenuEntry.onlineLocation,
                        AttachButton.ONLINE_LOCATION_KEY,
                        attachmentMultiMenuInfo.onlineLocation.actionName,
                        attachmentMultiMenuInfo.onlineLocation.onClick
                    );
                default:
                    return assertNever(defaultButtonType);
            }
        }
    );

    // Attachment suggestions (replyWith + MRU)
    const referenceItem: ClientItem = mailStore.items.get(
        viewState.referenceItemId && viewState.referenceItemId.Id
    );
    const replyWithSuggestions: AttachmentSuggestions =
        referenceItem && referenceItem.AttachmentSuggestions;
    const mruSuggestions: ReplyWithAttachmentSuggestion[] = getMRUAttachmentStore().attachments;

    if (!isSmimeCompose && (!!replyWithSuggestions || !!mruSuggestions)) {
        return buildReplyWithContextualMenuItems(
            viewState,
            replyWithSuggestions,
            mruSuggestions
        ).concat(defaultButtons);
    } else {
        return defaultButtons;
    }
}

function createAttachmentContextMenuItem(
    menuInfo: AttachmentMenuInfo,
    attachButtonType: AttachButton,
    actionName: string,
    onClick: () => void
): IContextualMenuItem {
    const aria = menuInfo.ariaLabel && menuInfo.ariaLabel();

    return {
        key: attachButtonType,
        name: menuInfo.text(),
        ariaLabel: aria,
        title: aria,
        iconProps: menuInfo.iconProps,
        onClick: (
            event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
            item?: SurfaceActionItem
        ) => {
            if (isFeatureEnabled('rp-customSurfaceActions')) {
                logUserAction(true /* isCompose */, actionName, item);
            }
            menuInfo.logUsage(AttachmentMenuEntrySource.ToolBar);
            onClick();
        },
    };
}

/**
 * Returns a list of button names (email/cloud/local etc) shown in the Attach button.
 * Used for logging. Needs to be consistent with getAttachmentCommandItems method.
 */
export function getDefaultButtonsList(): AttachButton[] {
    const defaultButtons: AttachButton[] = [
        AttachButton.COMPUTER_ATTACHMENT_KEY,
        AttachButton.ONLINE_LOCATION_KEY,
    ];

    if (attachmentMultiMenuEntry.uploadAndShareFromCloud.isEnabled()) {
        defaultButtons.push(AttachButton.UPLOAD_AND_SHARE_FROM_CLOUD_KEY);
    }

    return defaultButtons;
}

/**
 * Returns the list of buttons that needed to be shown in attach menu
 * in case of a S/MIME compose.
 */
export function getSmimeAttachButtonsList(): AttachButton[] {
    return [AttachButton.COMPUTER_ATTACHMENT_KEY];
}
